import {Grid, Tooltip, Typography} from '@material-ui/core';
import {InfoOutlined} from '@material-ui/icons';
import {makeStyles} from '@material-ui/core/styles';
import {labelTextStyle, NumberFormatCustom, SwitchSegmentedYesNo, TextBox} from '../../../Core/UI';
import React, {useEffect} from 'react';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {Prequal} from '../../../Core/mobx/ParameterStore/Prequal';
import {observer} from 'mobx-react-lite';
import EmploymentQuestions from '../../Questions/Employment';
import ExistingPolicies from '../../Questions/ExistingPolicies';
import {SelectInputElement} from './SelectInputElement';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/PrequalXref';
import {shouldShowHHIWarning, WarningMessage} from '@/Agent/Common/WarningMessage';
import RootStore from '../../../Core/mobx/RootStore';
import {AB_FLAGS} from '../../../Core/constants/abFlags';

const useStyles = makeStyles({
    icon: {
        marginRight: 4,
    },
    approved: {
        color: 'green',
    },
    denied: {
        color: 'red',
    },
    sectionTitle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 46,
        width: 46,
        borderRadius: '50%',
        backgroundColor: '#e8edec',
        color: '#456d69',
        marginRight: 21,
        fontSize: 26,
        textAlign: 'center',
    },
    sectionTitleWrapper: {
        display: 'flex',
        alignItems: 'center',
        textTransform: 'capitalize',
    },
    extraQuestionsBox: {
        margin: '56px 8px 0',
        boxShadow: '0px 0px 5px 0px #00000033',
    },
    extraQuestionsLabel: {
        marginBottom: 16,
        fontFamily: 'Fredoka',
        fontSize: 18,
        fontWeight: 500,
        lineHeight: '100%',
    },
    separatedLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        marginBottom: 12,
    },
    infoIcon: {
        marginLeft: 6,
        color: '#456d69',
        fontSize: 18,
        cursor: 'pointer',
    },
    tooltip: {
        maxWidth: 420,
        width: '100%',
        background: '#456d69',
        borderRadius: 10,
        padding: '16px 20px',
        margin: 0,
        color: '#fff',
        fontFamily: 'FredokaRegular',
        fontSize: 14,
        lineHeight: '20px',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
    },
    tooltipArrow: {
        color: '#456d69',
    },
    mt: {
        marginTop: 56,
    },
});

type FinancesProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Prequal;
    walkthroughData: any | null;
    hideNumbers?: boolean;
};

const Finances = observer(({valueChanged, parameters, walkthroughData, hideNumbers}: FinancesProps) => {
    const classes = useStyles();

    const rawHHI = parameters.incomeHouseholdText?.value ?? '';
    const hhi = Number(String(rawHHI).replace(/[,$]/g, '')) || 0;

    const monthlyBudget = Number(parameters.monthlyBudget?.value) || 0;

    // Get the AB flag value from the parameter store
    const lowerHouseholdIncomeLimit =
        RootStore.agentPortalStore.parameterStore.abFlags[AB_FLAGS.LOWER_HOUSEHOLD_INCOME_LIMIT] === true;

    const {exceeds: showPremiumWarning, threshold: premiumThreshold} = shouldShowHHIWarning({
        premium: monthlyBudget,
        annualHHI: hhi,
        premiumMode: 'Monthly',
        lowerHouseholdIncomeLimit,
    });

    // Sync walkthrough coverage amount into the parameter store on mount
    // so validation passes and the displayed value matches the store
    useEffect(() => {
        const wtCoverage = walkthroughData?.recommended?.coverageAmount;
        if (
            wtCoverage !== null &&
            wtCoverage !== undefined &&
            (parameters.recommendedCoverageAmount?.value === null ||
                parameters.recommendedCoverageAmount?.value === undefined)
        ) {
            valueChanged(parameters.recommendedCoverageAmount, wtCoverage);
        }
    }, [walkthroughData?.recommended?.coverageAmount, parameters.recommendedCoverageAmount?.value, valueChanged]);

    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    return (
        <Grid key={'Finances'} container spacing={2} style={{marginBottom: 24}}>
            <Grid item>
                <div id="prequal-finances-header" className={classes.sectionTitleWrapper}>
                    {!hideNumbers && <span className={classes.sectionTitle}>2</span>}
                    <Typography
                        style={{
                            fontSize: 24,
                            fontWeight: 700,
                            fontFamily: 'LoraSemiBold',
                            lineHeight: '120%',
                            letterSpacing: 0,
                        }}
                    >
                        Finances
                    </Typography>
                </div>
            </Grid>
            <div style={{width: '100%', marginBottom: 12}} />

            <EmploymentQuestions valueChanged={valueChanged} parameters={parameters} prequal />

            <Grid item xs={4}>
                <Typography className={classes.separatedLabel} style={labelTextStyle}>
                    <span>{required('')}</span>
                    <span>Annual Household Income</span>
                </Typography>
                <TextBox
                    id={'incomeHouseholdText'}
                    value={parameters.incomeHouseholdText?.value}
                    onChange={(value) =>
                        valueChanged({...parameters.incomeHouseholdText, name: 'incomeHouseholdText'}, value)
                    }
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                        style: {
                            height: 48,
                        },
                    }}
                    style={{
                        width: '100%',
                    }}
                />
            </Grid>
            <Grid item xs={4}>
                <Typography className={classes.separatedLabel} style={labelTextStyle}>
                    <span>{required('')}</span>
                    <span>Desired Monthly Budget</span>
                    <Tooltip
                        arrow
                        leaveDelay={200}
                        placement="right"
                        classes={{tooltip: classes.tooltip, arrow: classes.tooltipArrow}}
                        title={
                            <>
                                <p>
                                    <strong>Pre-populated from the customer&apos;s Online Estimate</strong> based on
                                    their Monthly Budget and a Standard Health Rating.
                                </p>
                                <p style={{marginTop: 8, opacity: 0.88}}>
                                    If they adjusted Coverage Amount online, this reflects the estimated premium for
                                    that selection.
                                </p>
                                <p style={{marginTop: 8, opacity: 0.88}}>
                                    Ask:{' '}
                                    <em>&quot;Do you have a monthly budget or amount that is comfortable?&quot;</em> —
                                    this ensures the product remains affordable.
                                </p>
                                <p style={{marginTop: 8, opacity: 0.88}}>
                                    Helps identify potential best fit products for agent review.
                                </p>
                            </>
                        }
                    >
                        <InfoOutlined className={classes.infoIcon} />
                    </Tooltip>
                </Typography>
                <TextBox
                    id={'monthlyBudget'}
                    value={parameters.monthlyBudget?.value}
                    onChange={(value) => valueChanged(parameters.monthlyBudget, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                        style: {
                            height: 48,
                        },
                    }}
                    style={{
                        width: '100%',
                    }}
                />
                {showPremiumWarning && <WarningMessage value={premiumThreshold} newDesign />}
            </Grid>

            <Grid item xs={4} className={classes.mt}>
                <Typography className={classes.separatedLabel} style={labelTextStyle}>
                    <span>{required('')}</span>
                    <span>Desired Coverage Amount</span>
                    <Tooltip
                        arrow
                        leaveDelay={200}
                        placement="right"
                        classes={{tooltip: classes.tooltip, arrow: classes.tooltipArrow}}
                        title={
                            <>
                                <p>
                                    <strong>Pre-populated from the Online Estimate</strong> — IUL Coverage Amount based
                                    on the customer&apos;s Monthly Budget and a Standard Health Rating.
                                </p>
                                <p style={{marginTop: 8, opacity: 0.88}}>
                                    If they adjusted Coverage Amount online, this reflects their selection.
                                </p>
                                <p style={{marginTop: 8, opacity: 0.88}}>
                                    If budget changed, ask: <em>&quot;Do you have a target coverage in mind?&quot;</em>{' '}
                                    If unsure, enter $0.
                                </p>
                                <p style={{marginTop: 8, opacity: 0.88}}>
                                    Helps identify potential best fit products for agent review.
                                </p>
                            </>
                        }
                    >
                        <InfoOutlined className={classes.infoIcon} />
                    </Tooltip>
                </Typography>
                <TextBox
                    id={'recommendedCoverageAmount'}
                    value={parameters.recommendedCoverageAmount?.value}
                    onChange={(value) => valueChanged(parameters.recommendedCoverageAmount, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                        style: {
                            height: 48,
                        },
                    }}
                    style={{
                        width: '100%',
                    }}
                />
            </Grid>
            <Grid item xs={4} className={classes.mt}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Are you currently in the military?',
                        ...parameters.isCurrentlyMilitary,
                        name: 'isCurrentlyMilitary',
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4} className={classes.mt}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Existing Coverage',
                        ...parameters.existingCoverage,
                        name: 'existingCoverage',
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            {parameters.isCurrentlyMilitary.value && (
                <Grid
                    item
                    xs={12}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 34px',
                    }}
                >
                    <Grid item xs={10} sm={8} md={6} lg={5}>
                        <Typography className={classes.extraQuestionsLabel}>
                            Are you currently in the military?
                        </Typography>
                        <SwitchSegmentedYesNo
                            param={{
                                label: 'Current or future deployment orders',
                                ...parameters.currentDeploymentOrders,
                                name: 'currentDeploymentOrders',
                            }}
                            required={required('')}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                </Grid>
            )}
            {parameters.existingCoverage.value && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>Existing Coverage</Typography>
                    </Grid>
                    <Grid container item xs={12} spacing={4} style={{marginBottom: 32}}>
                        <Grid item xs={6}>
                            <SelectInputElement
                                param={{
                                    label: 'Policy Type',
                                    ...parameters.existingPolicyType,
                                    name: 'existingPolicyType',
                                    options: fieldOptions.existingPolicyType.options,
                                }}
                                required={required('')}
                                valueChanged={valueChanged}
                                newLabelType
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography className={classes.separatedLabel} style={labelTextStyle}>
                                <span>{required('')}</span>
                                <span>Coverage amount of your existing policy</span>
                            </Typography>
                            <TextBox
                                id={'existingCoverageAmount'}
                                placeholder="Coverage Amount Of Your Existing Policy"
                                value={parameters.existingCoverageAmount?.value}
                                onChange={(value) =>
                                    valueChanged(
                                        {...parameters.existingCoverageAmount, name: 'existingCoverageAmount'},
                                        value,
                                    )
                                }
                                InputProps={{
                                    inputComponent: NumberFormatCustom,
                                    style: {
                                        height: 48,
                                    },
                                }}
                                style={{
                                    width: '100%',
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={6}>
                        <SwitchSegmentedYesNo
                            param={{
                                label: 'Replacing Existing Policy?',
                                ...parameters.replacingExistingPolicy,
                                name: 'replacingExistingPolicy',
                            }}
                            required={required('')}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
});

export default Finances;
